"use client";

import React, { useState } from "react";
import {
  ChevronRight,
  ChevronLeft,
  Shield,
  Briefcase,
  Truck,
  Check,
  MessageCircle,
  Copy,
  Building2,
  Palette,
  FileText,
  Award,
} from "lucide-react";



// ── Editable data ──────────────────────────────────────────────
const BANK_DETAILS = {
  accountName: "Luzuko Christopher Dlakavu",
  bank: "GoTyme Bank",
  accountNumber: "51055173146",
  branchCode: "678910",
  reference: "Use your full name as payment reference",
};

const WHATSAPP_NUMBER = "27659988773";

interface Course {
  id: string;
  name: string;
  icon: React.ElementType;
  fee: string;
  partners: string[];
  category: string;
}

const COURSES: Course[] = [
  {
    id: "security-grade-b",
    name: "Security Guarding — Grade B",
    icon: Shield,
    fee: "Contact for pricing",
    partners: ["Nkonyane Security Solutions"],
    category: "Security & Safety",
  },
  {
    id: "security-special",
    name: "Security — Special Course",
    icon: Shield,
    fee: "Contact for pricing",
    partners: ["Nkonyane Security Solutions"],
    category: "Security & Safety",
  },
  {
    id: "psira-application",
    name: "PSIRA Application",
    icon: Shield,
    fee: "Contact for pricing",
    partners: ["Nkonyane Security Solutions"],
    category: "Security & Safety",
  },
  {
    id: "edc",
    name: "EDC",
    icon: Briefcase,
    fee: "Contact for pricing",
    partners: ["Tabane"],
    category: "Security & Safety",
  },
  {
    id: "forklift",
    name: "Forklift Operator",
    icon: Truck,
    fee: "R1,500 + R200 registration",
    partners: ["M&J Machinery Operator Training Academy"],
    category: "Machinery Operator Training",
  },
  {
    id: "reach-truck",
    name: "Reach Truck Operator",
    icon: Truck,
    fee: "R2,500 + R200 registration",
    partners: ["M&J Machinery Operator Training Academy"],
    category: "Machinery Operator Training",
  },
  {
    id: "tlb",
    name: "TLB Operator",
    icon: Truck,
    fee: "R3,500 + R200 registration",
    partners: ["M&J Machinery Operator Training Academy"],
    category: "Machinery Operator Training",
  },
  {
    id: "roller-compactor",
    name: "Roller Compactor Operator",
    icon: Truck,
    fee: "R3,500 + R200 registration",
    partners: ["M&J Machinery Operator Training Academy"],
    category: "Machinery Operator Training",
  },
  {
    id: "front-end-loader",
    name: "Front-End Loader Operator",
    icon: Truck,
    fee: "R4,000 + R200 registration",
    partners: ["M&J Machinery Operator Training Academy"],
    category: "Machinery Operator Training",
  },
  {
    id: "excavator",
    name: "Excavator Operator",
    icon: Truck,
    fee: "R4,500 + R200 registration",
    partners: ["M&J Machinery Operator Training Academy"],
    category: "Machinery Operator Training",
  },
  {
    id: "dump-truck",
    name: "Dump Truck Operator",
    icon: Truck,
    fee: "R6,000 + R200 registration",
    partners: ["M&J Machinery Operator Training Academy"],
    category: "Machinery Operator Training",
  },
  {
    id: "grader",
    name: "Grader Operator",
    icon: Truck,
    fee: "R7,000 + R200 registration",
    partners: ["M&J Machinery Operator Training Academy"],
    category: "Machinery Operator Training",
  },
  {
    id: "business-registration",
    name: "Business Registration (CIPC)",
    icon: Building2,
    fee: "Contact for pricing",
    partners: ["Register directly with us"],
    category: "Business Services",
  },
  {
    id: "logo-design",
    name: "Logo Design",
    icon: Palette,
    fee: "Contact for pricing",
    partners: ["Register directly with us"],
    category: "Business Services",
  },
  {
    id: "business-profile",
    name: "Business Profile",
    icon: FileText,
    fee: "Contact for pricing",
    partners: ["Register directly with us"],
    category: "Business Services",
  },
  {
    id: "business-proposal",
    name: "Business Proposal",
    icon: FileText,
    fee: "Contact for pricing",
    partners: ["Register directly with us"],
    category: "Business Services",
  },
  {
    id: "compliance",
    name: "Business Compliance (Tax, BEE & more)",
    icon: Award,
    fee: "Contact for pricing",
    partners: ["Register directly with us"],
    category: "Business Services",
  },
];

const CATEGORY_ORDER = ["Security & Safety", "Machinery Operator Training", "Business Services"];

const NAVY = "#0F2A45";
const GOLD = "#C9A24B";
const CREAM = "#F7F4EE";

interface StepDotsProps {
  step: number;
}

function StepDots({ step }: StepDotsProps) {
  return (
    <div className="flex gap-1.5 justify-center mb-4">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-1.5 rounded-full transition-all duration-300"
          style={{
            width: i === step ? 20 : 6,
            backgroundColor: i <= step ? GOLD : "#3A4E63",
          }}
        />
      ))}
    </div>
  );
}

interface FormState {
  name: string;
  idNumber: string;
  phone: string;
  email: string;
}

interface FormErrors {
  name?: string;
  idNumber?: string;
  phone?: string;
}

export default function LuzRegistration() {
  const [step, setStep] = useState(0);
  const [course, setCourse] = useState<Course | null>(null);
  const [partner, setPartner] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({ name: "", idNumber: "", phone: "", email: "" });
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [refCode, setRefCode] = useState("");
  const [copied, setCopied] = useState(false);

  const pick = (c: Course) => {
    setCourse(c);
    setStep(1);
  };

  const pickPartner = (p: string) => {
    setPartner(p);
    setStep(2);
  };

  const validate = () => {
    const e: FormErrors = {};
    if (!form.name.trim()) e.name = "Enter your full name";
    if (!form.idNumber.trim()) e.idNumber = "Enter your ID number";
    if (!form.phone.trim()) e.phone = "Enter a phone number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    const code = "LUZ-" + Math.random().toString(36).slice(2, 8).toUpperCase();
    setRefCode(code);
    try {
      const storage = (window as any).storage;
      if (storage) {
        await storage.set(
          `registration:${code}`,
          JSON.stringify({
            code,
            course: course?.name,
            partner,
            ...form,
            submittedAt: new Date().toISOString(),
          }),
          true
        );
      }
    } catch (err) {
      console.error("Could not save registration", err);
    }
    setSaving(false);
    setStep(3);
  };

  const waMessage = encodeURIComponent(
    `Hi LUZ Training Academy, I'd like to register.\n\nRef: ${refCode}\nCourse: ${course?.name}\nPartner: ${partner}\nName: ${form.name}\nID: ${form.idNumber}\nPhone: ${form.phone}\nEmail: ${form.email || "—"}`
  );
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${waMessage}`;

  const copyBank = () => {
    const text = `${BANK_DETAILS.accountName}\n${BANK_DETAILS.bank}\nAcc: ${BANK_DETAILS.accountNumber}\nBranch: ${BANK_DETAILS.branchCode}\nRef: ${form.name || BANK_DETAILS.reference}`;
    if (typeof navigator !== "undefined") {
      navigator.clipboard?.writeText(text);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="min-h-screen w-full flex items-start justify-center py-6 px-3"
      style={{ backgroundColor: "#0A1E30" }}
    >
      <div
        className="w-full max-w-sm rounded-2xl overflow-hidden shadow-xl"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-4" style={{ backgroundColor: NAVY }}>
          <div className="flex items-center gap-2 mb-1">
            <div
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: GOLD }}
            />
            <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: GOLD }}>
              LUZ Training Academy
            </p>
          </div>
          <h1 className="text-white text-lg leading-tight font-semibold">
            {step === 0 && "Choose your course"}
            {step === 1 && "Choose your training partner"}
            {step === 2 && "Your details"}
            {step === 3 && "You're almost registered"}
          </h1>
          <p className="text-[11px] mt-1" style={{ color: "#8BA8C0" }}>
            Skills Today • Opportunities Tomorrow • Success Always
          </p>
        </div>

        <div className="px-5 py-5" style={{ backgroundColor: CREAM }}>
          <StepDots step={step} />

          {/* Step 0: catalog */}
          {step === 0 && (
            <div className="flex flex-col gap-5">
              {CATEGORY_ORDER.map((cat) => (
                <div key={cat}>
                  <p
                    className="text-[11px] uppercase tracking-wide font-semibold mb-2"
                    style={{ color: GOLD }}
                  >
                    {cat}
                  </p>
                  <div className="flex flex-col gap-2">
                    {COURSES.filter((c) => c.category === cat).map((c) => {
                      const CourseIcon = c.icon;
                      return (
                        <button
                          key={c.id}
                          onClick={() => pick(c)}
                          className="flex items-center gap-3 p-3 rounded-xl text-left transition-colors hover:bg-white focus:outline-none focus-visible:ring-2"
                          style={{ backgroundColor: "#FFFFFF", border: "1px solid #E4DFD3" }}
                        >
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                            style={{ backgroundColor: "#EEE8DC" }}
                          >
                            <CourseIcon size={16} color={GOLD} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold" style={{ color: NAVY }}>
                              {c.name}
                            </p>
                            <p className="text-[11px] text-gray-500">{c.fee}</p>
                          </div>
                          <ChevronRight size={16} color="#9CA3AF" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 1: partner */}
          {step === 1 && course && (
            <div>
              <button
                onClick={() => setStep(0)}
                className="flex items-center gap-1 text-[12px] mb-4"
                style={{ color: GOLD }}
              >
                <ChevronLeft size={14} /> Back to courses
              </button>
              <p className="text-[13px] mb-3" style={{ color: NAVY }}>
                <strong style={{ color: NAVY }}>{course.name}</strong> is offered through the partner below. Registration is handled by LUZ Training Academy on your behalf.
              </p>
              <div className="flex flex-col gap-2">
                {course.partners.map((p) => (
                  <button
                    key={p}
                    onClick={() => pickPartner(p)}
                    className="flex items-center justify-between p-3 rounded-xl text-left hover:bg-white"
                    style={{ backgroundColor: "#FFFFFF", border: "1px solid #E4DFD3" }}
                  >
                    <span className="text-[13px] font-semibold" style={{ color: NAVY }}>{p}</span>
                    <ChevronRight size={16} color="#9CA3AF" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: form */}
          {step === 2 && (
            <div>
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-1 text-[12px] mb-4"
                style={{ color: GOLD }}
              >
                <ChevronLeft size={14} /> Back
              </button>
              <div className="flex flex-col gap-3">
                {[
                  { key: "name" as keyof FormState, label: "Full name", ph: "e.g. Thandiwe Mavuso" },
                  { key: "idNumber" as keyof FormState, label: "ID number", ph: "13-digit SA ID number" },
                  { key: "phone" as keyof FormState, label: "Phone number", ph: "0XX XXX XXXX" },
                  { key: "email" as keyof FormState, label: "Email (optional)", ph: "name@example.com" },
                ].map((f) => (
                  <div key={f.key}>
                    <label className="text-[11px] uppercase tracking-wide" style={{ color: "#6B7280" }}>
                      {f.label}
                    </label>
                    <input
                      value={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      placeholder={f.ph}
                      className="w-full mt-1 px-3 py-2 rounded-lg text-[13px] focus:outline-none bg-white"
                      style={{
                        border: `1px solid ${errors[f.key as keyof FormErrors] ? "#C0503E" : "#E4DFD3"}`,
                        color: NAVY,
                      }}
                    />
                    {errors[f.key as keyof FormErrors] && (
                      <p className="text-[11px] mt-1" style={{ color: "#C0503E" }}>
                        {errors[f.key as keyof FormErrors]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={submit}
                disabled={saving}
                className="w-full mt-5 py-3 rounded-xl text-[13px] font-semibold tracking-wide text-white"
                style={{ backgroundColor: saving ? "#8BA8C0" : NAVY }}
              >
                {saving ? "Saving…" : "Continue to payment details"}
              </button>
            </div>
          )}

          {/* Step 3: confirm */}
          {step === 3 && (
            <div className="flex flex-col gap-4">
              <div
                className="flex items-center gap-2 p-3 rounded-xl"
                style={{ backgroundColor: "#E8F5E9", border: "1px solid #C8E6C9" }}
              >
                <Check size={18} color="#3A7A3A" />
                <div>
                  <p className="text-[12px] font-semibold" style={{ color: "#2E7D32" }}>
                    Reference {refCode}
                  </p>
                  <p className="text-[11px] text-gray-600">
                    Send this to LUZ via WhatsApp to complete registration.
                  </p>
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-wide mb-2" style={{ color: "#6B7280" }}>
                  Payment details
                </p>
                <div
                  className="p-3 rounded-xl text-[12px]"
                  style={{ backgroundColor: "#FFFFFF", border: "1px solid #E4DFD3", color: NAVY, lineHeight: "1.8" }}
                >
                  <p>{BANK_DETAILS.accountName}</p>
                  <p>{BANK_DETAILS.bank}</p>
                  <p>Acc: {BANK_DETAILS.accountNumber}</p>
                  <p>Branch: {BANK_DETAILS.branchCode}</p>
                  <p style={{ color: GOLD, fontWeight: 600 }}>Ref: {form.name}</p>
                </div>
                <button
                  onClick={copyBank}
                  className="flex items-center gap-1 text-[11px] mt-2"
                  style={{ color: GOLD }}
                >
                  <Copy size={12} /> {copied ? "Copied!" : "Copy details"}
                </button>
              </div>

              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-[13px] font-semibold text-white"
                style={{ backgroundColor: "#25D366" }}
              >
                <MessageCircle size={16} /> Send registration on WhatsApp
              </a>

              <button
                onClick={() => {
                  setStep(0);
                  setCourse(null);
                  setPartner(null);
                  setForm({ name: "", idNumber: "", phone: "", email: "" });
                }}
                className="text-[12px] underline text-center"
                style={{ color: "#6B7280" }}
              >
                Register for another course
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
